import { Disclosure, Transition } from "@headlessui/react";
import { useState, useEffect } from "react";
import ArrowIcon from "src/assets/chevron-right.svg";
import { Badge, Button, Input } from "src/components";
import { cn } from "src/utils/utils";

interface FilterAccordion {
  title: string;
  onSelectingFilter?: (value: string[]) => void;
  defaultValue?: string[];
  validateInput?: (value: string) => boolean | string;
}

const InputFilterAccordion = ({ title, onSelectingFilter, defaultValue, validateInput }: FilterAccordion) => {
  const [inputValue, setInputValue] = useState("");
  const [inputValidationErr, setInputValidationErr] = useState("");
  const [enteredItems, setEnteredItems] = useState<string[]>([]);

  const handleInput = () => {
    const trimmedValue = inputValue.trim();

    const isNonEmpty = !!trimmedValue;
    const isUnique = !enteredItems.includes(trimmedValue);

    let validationError = "";
    let isValid = true;

    if (validateInput) {
      const validationResult = validateInput(trimmedValue);

      if (typeof validationResult === "string") {
        validationError = validationResult;
        isValid = false;
      } else if (typeof validationResult === "boolean") {
        isValid = validationResult;
        validationError = validationResult ? "" : "Invalid input";
      }
    }

    setInputValidationErr(validationError);

    if (isNonEmpty && isUnique && isValid) {
      setEnteredItems([...enteredItems, trimmedValue]);
      onSelectingFilter && onSelectingFilter([...enteredItems, trimmedValue]);
      setInputValue("");
      setInputValidationErr("");
    }
  };

  const removeFilterHandler = (e: any, value: any) => {
    e.stopPropagation();

    const updatedSelectedItems = [...enteredItems.filter((item) => item !== value)];

    setEnteredItems(updatedSelectedItems);
    onSelectingFilter && onSelectingFilter(updatedSelectedItems);
  };

  useEffect(() => {
    setEnteredItems(defaultValue || []);
  }, [defaultValue]);

  return (
    <Disclosure>
      {({ open }) => (
        <>
          <Disclosure.Button>
            <div className="text-[#49484C] flex justify-between items-center gap-2 font-semibold text-sm">
              <h4 className="flex items-center gap-2">
                {title}
                <Badge className="bg-[#d9cdf4] p-[0.3125rem] w-auto h-auto flex items-center justify-center rounded-full text-[0.625rem] font-semibold text-[#42267f]">
                  {enteredItems?.length || 0}
                </Badge>
              </h4>
              <div className="hover:bg-[#d9cdf4] rounded-full p-2">
                <img
                  src={ArrowIcon}
                  className={cn(`transform ${open ? "-rotate-90" : "rotate-90"} transition duration-300 ease-in-out`)}
                  alt="arrow down"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-3 mb-2 break-words">
              {enteredItems?.length > 0 &&
                enteredItems.map((item) => (
                  <Badge className="bg-[#D9CDF4] text-[#42267F] font-semibold text-sm py-[0.38rem] py-[0.62rem]" key={item}>
                    {item}
                    <span onClick={(e) => removeFilterHandler(e, item)} className="ml-3">
                      X
                    </span>
                  </Badge>
                ))}
            </div>
          </Disclosure.Button>
          <Transition
            enter="transition duration-300 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-300 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Disclosure.Panel>
              <div className="flex gap-2 items-start">
                <div className="flex flex-col gap-2 w-11/12">
                  <Input
                    placeholder={`Enter ${title}`}
                    value={inputValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value;
                      setInputValue(value);

                      if (!value.trim()) {
                        setInputValidationErr("");
                        return;
                      }
                    }}
                    onKeyDown={(e: any) => e.key === "Enter" && handleInput()}
                  />
                  <p className="text-xs text-red-600">{inputValidationErr}</p>
                </div>
                <Button onClick={handleInput} className="border py-1 bg-[#7F56D9] text-white">
                  Add
                </Button>
              </div>
            </Disclosure.Panel>
          </Transition>
        </>
      )}
    </Disclosure>
  );
};

export default InputFilterAccordion;
